import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { InsertColumnPosition } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import {
  ArrowLeft16,
  ArrowRight16,
  Clipboard16,
  Copy16,
  EyeSlash24,
  Info16,
  Pin16,
  Trash16,
} from '@makyoapp/makyo-icon/icon'
import { cn } from '@makyoapp/makyo-tw-ui'
import { DDItem } from '@makyoapp/makyo-ui'

import { IDB } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useReqError } from '@/hooks/useReqError'
import { generateColumnNameV2 } from '@/shared/ag-grid'
import { EVENT_KEYS } from '@/shared/custom-event'

import { useGridV2Context } from '../../contexts/GridV2Context'
import { GridDeleteModal } from '../modal/GridDeleteModal'
import { GridDuplicateColumnModal } from '../modal/GridDuplicateColumnModal'
import { GridEditModal } from '../modal/GridEditModal'
import { GridInsertColumn } from '../modal/GridInsertColumn'

function checkColumnInSnapshotOrQuery(dbClient: IDB, tableID: string, columnID: string) {
  const snapshotTables = dbClient.snapshotRefMap.get(tableID) ?? new Set()
  const queryTables = dbClient.queryRefMap.get(tableID) ?? new Set()
  const arrayTables = Array.from([...snapshotTables, ...queryTables])

  if (arrayTables.length === 0) return false

  const isColumnUsed = Array.from(arrayTables).some((tableId) => {
    const table = dbClient.tablesMap.get(tableId)
    if (!table) return false
    return table.settings._Columns.some((c) => c._id === columnID)
  })

  return isColumnUsed
}

interface GridHeaderMenu {
  activeColumn: ColumnAdapter
}

export const GridHeaderMenu = ({ activeColumn }: GridHeaderMenu) => {
  const printAPIError = useReqError()
  const { t } = useTranslation()

  const [isDupLoading, setIsDupLoading] = useState(false)

  const { dbClient } = useDbClientContext()
  const {
    table: activeTable,
    modalGridHeaderMenu,
    setModalGridHeaderMenu,
    handleFreezeColumn,
    handleHideColumn,
    handleDuplicateColumn,
    setColumnToInsert,
    handleCloseMenu,
    showTableRowIDs,
    setShowTableRowIDs,
    tableId,
    columnClipboard,
    setColumnClipboard,
    handlePasteColumn,
  } = useGridV2Context()

  const isSnapshot = !!activeTable?.isSnapshot
  const isQuery = !!activeTable?.isQuery

  const enableFreeze = !isQuery && !activeColumn?.isSourceColumn

  const isMutationDisabled = useMemo(() => {
    if (!activeColumn) return false

    if (isSnapshot) return true

    // only allow mutation for advanced columns in query
    if (isQuery) {
      return activeColumn.isSourceColumn
    }

    return false
    //
  }, [activeColumn, isSnapshot, isQuery])

  const slugColumnTotal = activeTable?.columns?.filter((c) => c.type === ColumnTypes.Slug).length
  const isLastSlugColumn = activeColumn?.type === ColumnTypes.Slug && slugColumnTotal === 1

  const onDuplicate = async () => {
    if (!activeTable || !activeColumn) return

    setIsDupLoading(true)

    const newColumnName = generateColumnNameV2(`${activeColumn.name} copy`, activeTable)

    handleDuplicateColumn(activeColumn.id, newColumnName)
      .catch(printAPIError)
      .finally(() => {
        setIsDupLoading(false)
        handleCloseMenu()
        toast.success(t('Column duplicated'))
      })
  }

  const handleDuplicate = () => {
    if (!activeColumn) return

    setModalGridHeaderMenu({ columnId: activeColumn.id, type: 'duplicate' })
  }

  const onEditColumnSettings = () => {
    if (activeColumn) {
      setModalGridHeaderMenu({ columnId: activeColumn.id, type: 'edit' })
    }
  }

  const onInsertColumnLeft = () => {
    if (activeColumn) {
      setColumnToInsert({
        positionToInsert: InsertColumnPosition.Left,
        columnID: activeColumn.id,
      })
      setModalGridHeaderMenu({ columnId: activeColumn.id, type: 'insert' })
    }
  }

  const onInsertColumnRight = () => {
    if (activeColumn) {
      setColumnToInsert({
        positionToInsert: InsertColumnPosition.Right,
        columnID: activeColumn.id,
      })
      setModalGridHeaderMenu({ columnId: activeColumn.id, type: 'insert' })
    }
  }

  const onToggleFreezeColumn = () => {
    if (activeColumn) {
      handleFreezeColumn(activeColumn.id)
    }
  }

  const onHideColumn = () => {
    if (activeColumn && activeColumn.id !== '_id') {
      handleHideColumn(activeColumn.id, true)
      handleCloseMenu()
    }

    if (activeColumn && activeColumn.id === '_id') {
      const isShowRowID = showTableRowIDs?.includes(tableId) ?? false

      if (isShowRowID) {
        setShowTableRowIDs((prev) => prev?.filter((id) => id !== tableId))
      } else {
        setShowTableRowIDs((prev) => {
          return Array.from(new Set([...(prev || []), tableId]))
        })
      }
      handleCloseMenu()
    }
  }

  const onDeleteColumn = () => {
    if (!activeTable || !dbClient || !activeColumn) return

    // check if current table is sanpshot, and this column is being used at least in one snapshot table
    const isUsedInSnapshot = checkColumnInSnapshotOrQuery(dbClient, activeTable.id, activeColumn.id)
    if (isUsedInSnapshot) {
      window.dispatchEvent(
        new CustomEvent(EVENT_KEYS.SNAPSHOT_ALERT_MODAL, {
          detail: { columnId: activeColumn.id, type: 'snapshot-column-used' },
        })
      )
      return
    }

    if (activeColumn) {
      setModalGridHeaderMenu({ columnId: activeColumn.id, type: 'remove' })
    }
  }

  const onTranslateColumn = () => {}

  const onCopyColumn = () => {
    setColumnClipboard({ tbl: activeTable?.id || '', col: activeColumn.id })
    toast(t('Column copied to clipboard'))
    handleCloseMenu()
  }

  const onPasteColumnLeft = async () => {
    if (!columnClipboard) return

    await handlePasteColumn(activeColumn.id, InsertColumnPosition.Left)
    toast.success(t('Column pasted successfully'))
    handleCloseMenu()
  }

  const onPasteColumnRight = async () => {
    if (!columnClipboard) return

    await handlePasteColumn(activeColumn.id, InsertColumnPosition.Right)
    toast.success(t('Column pasted successfully'))
    handleCloseMenu()
  }

  const showClipboardButton = columnClipboard && activeTable?.id !== columnClipboard.tbl && !activeTable?.isSnapshot

  // display modal based on active columns,
  // it will triggered by some of the menu items below
  if (modalGridHeaderMenu && modalGridHeaderMenu.columnId === activeColumn.id) {
    return (
      <>
        {modalGridHeaderMenu.type === 'insert' && <GridInsertColumn />}
        {modalGridHeaderMenu.type === 'edit' && <GridEditModal column={activeColumn} />}
        {modalGridHeaderMenu.type === 'remove' && <GridDeleteModal column={activeColumn} />}
        {modalGridHeaderMenu.type === 'duplicate' && <GridDuplicateColumnModal column={activeColumn} />}
      </>
    )
  }

  return (
    <div className='text-m-m'>
      {activeColumn?.id !== '_id' && (
        <>
          <DDItem
            id={`grid-column-menu-edit-setting`}
            onClick={onEditColumnSettings}
            className={cn('flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full', isMutationDisabled && 'hidden!')}
            disabled={isMutationDisabled}
          >
            <Info16 />
            <span>
              <Trans>Edit Column Settings</Trans>
            </span>
          </DDItem>

          <DDItem
            id={`grid-column-menu-duplicate`}
            onClick={handleDuplicate}
            className={cn('flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full', isSnapshot && 'hidden!')}
            disabled={isSnapshot}
          >
            {isDupLoading ? <span className='loading' /> : <Copy16 />}
            <span>{<Trans>Duplicate Column</Trans>}</span>
          </DDItem>

          <DDItem
            id={`grid-column-menu-insert-left`}
            onClick={onInsertColumnLeft}
            className={cn('flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full', isMutationDisabled && 'hidden!')}
            disabled={isMutationDisabled}
          >
            <ArrowLeft16 />
            <span>
              <Trans>Insert Column Left</Trans>
            </span>
          </DDItem>

          <DDItem
            id={`grid-column-menu-insert-right`}
            onClick={onInsertColumnRight}
            className={cn('flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full', isMutationDisabled && 'hidden!')}
            disabled={isMutationDisabled}
          >
            <ArrowRight16 />
            <span>
              <Trans>Insert Column Right</Trans>
            </span>
          </DDItem>

          <hr className='hr' />

          <DDItem
            id={`grid-column-menu-copy`}
            onClick={onCopyColumn}
            className={cn('flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full', isSnapshot && 'hidden!')}
            disabled={isSnapshot}
          >
            <Copy16 />
            <span>
              <Trans>Copy Column</Trans>
            </span>
          </DDItem>

          {showClipboardButton && (
            <>
              <DDItem
                id={`grid-column-menu-insert-left`}
                onClick={onPasteColumnLeft}
                className={cn(
                  'flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full',
                  isMutationDisabled && 'hidden!'
                )}
                disabled={isMutationDisabled}
              >
                <Clipboard16 />
                <span>
                  <Trans>Paste Column Left</Trans>
                </span>
              </DDItem>
              <DDItem
                id={`grid-column-menu-insert-right`}
                onClick={onPasteColumnRight}
                className={cn(
                  'flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full',
                  isMutationDisabled && 'hidden!'
                )}
                disabled={isMutationDisabled}
              >
                <Clipboard16 />
                <span>
                  <Trans>Paste Column Right</Trans>
                </span>
              </DDItem>
            </>
          )}

          {/* <hr className='hr' />

          <DDItem
            id={`grid-column-translate`}
            onClick={onTranslateColumn}
            className='flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full'
          >
            <Openai24 width={16} height={16} />
            <span>
              <Trans>{t('Translate this column')}</Trans>
            </span>
          </DDItem> */}

          <hr className='hr' />

          {enableFreeze && (
            <DDItem
              id={`grid-column-menu-freeze`}
              onClick={onToggleFreezeColumn}
              className='flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full'
            >
              <Pin16 />
              <span>{activeColumn?.isFrozen ? <Trans>Unfreeze Column</Trans> : <Trans>Freeze Column</Trans>}</span>
            </DDItem>
          )}
        </>
      )}

      <DDItem
        id={`grid-column-menu-hide`}
        onClick={onHideColumn}
        className='flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full'
      >
        <EyeSlash24 width={16} height={16} />
        <span>
          <Trans>Hide Column</Trans>
        </span>
      </DDItem>

      {!isLastSlugColumn && activeColumn?.id !== '_id' && (
        <DDItem
          id={`grid-column-menu-remove`}
          className={cn(
            'flex-c hover:bg-primary-50 dark:hover:bg-slate-700 w-full',
            isSnapshot ? 'hidden!' : !activeColumn?.canDelete && 'opacity-50'
          )}
          disabled={isSnapshot || !activeColumn?.canDelete}
          onClick={onDeleteColumn}
        >
          <Trash16 />
          <span>
            <Trans>Delete Column</Trans>
          </span>
        </DDItem>
      )}
    </div>
  )
}
